import { useMemo, useState, type CSSProperties } from "react";
import { ArrowUpRight, Calendar, Check, Clock, Lock, Sparkles, Type, Video, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

const srOnly =
  "absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0 [clip:rect(0,0,0,0)]";

interface BodyProps {
  company: string;
  hostname?: string;
  icpTitle?: string;
  /** Brand assets pulled from the trial scrape — when present, the card
   *  themes itself with the visitor's actual logo + primary color so the
   *  preview matches what their real /book/:slug page would look like. */
  logoUrl?: string;
  primaryColor?: string;  // "#rrggbb"
  accentColor?: string;   // "#rrggbb" — secondary brand color
  /** Optional brand font family (already sanitized by the server). Applied to
   *  the headline + company name so the preview *feels* like the visitor's
   *  site even when we can't load the webfont itself. */
  fontFamily?: string;
  /** "scraped" = real brand color from the site; "derived" = hashed-from-host fallback. */
  colorSource?: "scraped" | "derived";
  /** "scraped" = real brand logo; "favicon" = Google favicon fallback. */
  logoSource?: "scraped" | "favicon";
  /** When true, hide chrome row + close affordances and tighten visuals so
   *  the card nests inline under another element (e.g. an email card). */
  compact?: boolean;
  /** Inline-mode "Open full screen" affordance — when provided, renders a
   *  small text button below the card that triggers this callback. */
  onOpenFullscreen?: () => void;
}

interface Props extends Omit<BodyProps, "compact" | "onOpenFullscreen"> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DAY_HEADERS = ["S", "M", "T", "W", "T", "F", "S"];
const TIMES = ["9:00 AM", "10:30 AM", "1:00 PM", "2:30 PM", "4:00 PM"];

/* --------------------------------- color utils -------------------------- */

// Returns "h s% l%" — the format Tailwind's `hsl(var(--primary))` expects.
// Mirrors the converter in src/pages/BookPage.tsx for visual parity.
function hexToHslVar(hex: string | undefined): string | null {
  if (!hex) return null;
  const h = hex.replace("#", "");
  const norm = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  if (norm.length !== 6) return null;
  const r = parseInt(norm.slice(0, 2), 16) / 255;
  const g = parseInt(norm.slice(2, 4), 16) / 255;
  const b = parseInt(norm.slice(4, 6), 16) / 255;
  if ([r, g, b].some(v => Number.isNaN(v))) return null;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hh = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: hh = ((b - r) / d + 2); break;
      case b: hh = ((r - g) / d + 4); break;
    }
    hh /= 6;
  }
  return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function readableForegroundHsl(hex: string | undefined): string | null {
  if (!hex) return null;
  const h = hex.replace("#", "");
  const norm = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  if (norm.length !== 6) return null;
  const r = parseInt(norm.slice(0, 2), 16);
  const g = parseInt(norm.slice(2, 4), 16);
  const b = parseInt(norm.slice(4, 6), 16);
  if ([r, g, b].some(v => Number.isNaN(v))) return null;
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.55 ? "0 0% 10%" : "0 0% 100%";
}

/**
 * Mock of the visitor's *own* AutoPitch-hosted booking page, themed with
 * their company name, logo, brand colors, and (when scraped) brand font.
 *
 * Brand-presence strategy (the whole point of this component):
 * 1. **Tall hero band** with a strong primary→accent gradient — the brand
 *    color must *land*, not whisper.
 * 2. **48px logo chip** matching the real BookPage prominence.
 * 3. **Company name in `text-primary`** inside the H2 so the brand color
 *    appears twice above the fold.
 * 4. **Accent color** lights the social-proof pill so we use both scraped
 *    colors, not just primary.
 * 5. **Brand font** applied inline to headline + company name as a cue.
 * 6. **"Branded with…" affordance chip** in the chrome row makes it
 *    explicit that we pulled the visitor's real brand from their site.
 */
export function BookingPagePreviewBody({
  company,
  hostname,
  icpTitle,
  logoUrl,
  primaryColor,
  accentColor,
  fontFamily,
  colorSource,
  logoSource,
  compact = false,
  onOpenFullscreen,
}: BodyProps) {
  const now = useMemo(() => new Date(), []);
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthName = now.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const today = now.getDate();

  const [selectedDay, setSelectedDay] = useState<number>(() => Math.min(today + 3, daysInMonth));
  const [selectedTime, setSelectedTime] = useState<string>(TIMES[1]);
  const [logoBroken, setLogoBroken] = useState(false);

  const subhead = icpTitle
    ? `15-minute intro for ${icpTitle.toLowerCase().replace(/\.$/, "")}.`
    : "Pick a time that works — we'll send a calendar invite with the meeting link.";

  // Inject brand color as CSS vars; Tailwind classes (`bg-primary`,
  // `text-primary`, `bg-accent`, etc.) auto-resolve. When a color is missing
  // we leave the var unset and fall back to the Auto Pitch palette.
  const primaryHsl = hexToHslVar(primaryColor);
  const primaryFgHsl = readableForegroundHsl(primaryColor);
  const accentHsl = hexToHslVar(accentColor);
  const accentFgHsl = readableForegroundHsl(accentColor);
  const cardStyle: CSSProperties = {};
  if (primaryHsl) (cardStyle as Record<string, string>)["--primary"] = primaryHsl;
  if (primaryFgHsl) (cardStyle as Record<string, string>)["--primary-foreground"] = primaryFgHsl;
  if (accentHsl) (cardStyle as Record<string, string>)["--accent"] = accentHsl;
  if (accentFgHsl) (cardStyle as Record<string, string>)["--accent-foreground"] = accentFgHsl;

  const showLogo = !!logoUrl && !logoBroken;

  // Brand font is applied inline; safe fallback chain keeps things readable
  // even when the OS doesn't have the family installed.
  const brandFontStyle: CSSProperties | undefined = fontFamily
    ? { fontFamily: `${fontFamily}, system-ui, sans-serif` }
    : undefined;

  // "Branded with…" affordance — explicitly labels each piece by source so
  // the visitor knows which parts came from their real site vs. were derived
  // as a tasteful fallback (favicon / hashed color).
  const brandedBits: string[] = [];
  if (showLogo) brandedBits.push(logoSource === "favicon" ? "your site icon" : "your logo");
  if (primaryColor) {
    brandedBits.push(colorSource === "derived" ? "a sample brand color" : primaryColor.toUpperCase());
  }
  if (fontFamily) brandedBits.push(fontFamily);
  const hasAnyBranding = brandedBits.length > 0;
  // Only show the "Neutral preview" message when *nothing* came back — with
  // favicon + derived-color fallbacks this should be rare.
  const isFullyNeutral = !showLogo && !primaryColor && !fontFamily;

  return (
    <div className="w-full">
      {/* Chrome row — Auto Pitch palette, NOT themed. Communicates that
          this is *their* page hosted on *our* platform. Hidden in compact
          (inline) mode where the parent already provides framing. */}
      {!compact && (
        <div className="flex items-center justify-between gap-2 mb-2 px-1">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground min-w-0">
            <span className="relative flex h-1.5 w-1.5 shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
            </span>
            <span className="truncate">Booking page · {company}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <Lock className="h-3 w-3" aria-hidden /> sample
            </span>
            {onOpenFullscreen && (
              <button
                type="button"
                onClick={onOpenFullscreen}
                aria-label="Close preview"
                className="inline-flex items-center gap-1 rounded-full bg-card border border-border px-3 py-1.5 text-[11px] font-medium text-foreground hover:bg-muted active:scale-95 transition-all shadow-sm"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
                Close
              </button>
            )}
          </div>
        </div>
      )}

          {/* "Branded with…" affordance — explicitly surfaces what we
              pulled from the visitor's site so the brand work is *visible*. */}
          {hasAnyBranding ? (
            <div className="mb-2 px-1 flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-primary shrink-0" aria-hidden />
              <span className="truncate">
                Auto-branded with{" "}
                {brandedBits.map((bit, i) => (
                  <span key={bit}>
                    <span className="text-foreground/80 font-medium">{bit}</span>
                    {i < brandedBits.length - 1 && <span className="text-muted-foreground/85"> · </span>}
                  </span>
                ))}{" "}
                from {hostname || "your site"}
                {(colorSource === "derived" || logoSource === "favicon") && (
                  <span className="text-muted-foreground/70"> · polish in Settings after signup</span>
                )}
              </span>
            </div>
          ) : isFullyNeutral ? (
            <div className="mb-2 px-1 flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <Type className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">
                Neutral preview · Add your brand color &amp; logo in Settings after signup
              </span>
            </div>
          ) : null}

          {/* Themed booking card */}
          <div
            className="bg-card border border-border rounded-2xl overflow-hidden font-sans shadow-2xl shadow-primary/10"
            style={cardStyle}
          >
            {/* Tall branded hero — strong gradient so the brand color
                actually lands. Uses primary→accent so both scraped colors
                show up here, not just primary. */}
            <div className="relative w-full h-24 bg-gradient-to-br from-primary/60 via-primary/35 to-accent/25 overflow-hidden">
              {!compact && onOpenFullscreen && (
                <button
                  type="button"
                  onClick={onOpenFullscreen}
                  aria-label="Close booking preview"
                  className="absolute right-3 top-3 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/70 bg-background/95 text-foreground shadow-lg backdrop-blur-sm transition-all hover:bg-muted active:scale-95"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              )}

              {/* Subtle radial highlight for depth */}
              <div
                className="absolute inset-0 opacity-40"
                style={{
                  background:
                    "radial-gradient(circle at 80% 20%, hsl(var(--primary) / 0.4), transparent 60%)",
                }}
                aria-hidden
              />

              {/* Logo chip — bumped to 48px to match real BookPage prominence */}
              <div className="absolute bottom-3 left-3 flex items-center gap-2.5">
                <div className="h-12 w-12 rounded-xl border border-border/60 bg-background/95 backdrop-blur-sm flex items-center justify-center overflow-hidden shadow-lg">
                  {showLogo ? (
                    <img
                      src={logoUrl}
                      alt=""
                      className="h-9 w-9 object-contain"
                      referrerPolicy="no-referrer"
                      loading="eager"
                      onError={() => setLogoBroken(true)}
                    />
                  ) : (
                    <span
                      className="text-lg font-bold text-primary"
                      style={brandFontStyle}
                    >
                      {company.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                {hostname && (
                  <div className="flex flex-col leading-tight">
                    <span
                      className="text-sm font-semibold text-foreground"
                      style={brandFontStyle}
                    >
                      {company}
                    </span>
                    <span className="text-[11px] text-foreground/70 font-mono">
                      {hostname}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 sm:p-5">
              {/* Step dots */}
              <div className="flex gap-2 mb-4">
                {[1, 2, 3].map(s => (
                  <div
                    key={s}
                    className={`w-2 h-2 rounded-full ${
                      s === 1 ? "bg-primary" : "bg-muted"
                    }`}
                  />
                ))}
              </div>

              {/* Headline — company name in BRAND COLOR + brand font for
                  maximum brand-recognition above the fold. */}
              <h2
                className="text-lg sm:text-xl font-bold text-foreground mb-1 leading-tight"
                style={brandFontStyle}
              >
                Book a 15-min intro with{" "}
                <span className="text-primary">{company}</span>
              </h2>
              <p className="text-xs text-muted-foreground mb-3">{subhead}</p>

              {/* Social proof — uses ACCENT color so both brand colors are
                  visible. Falls back gracefully to primary when no accent. */}
              <div
                className={`flex items-center gap-2 rounded-lg px-3 py-2 mb-4 text-[11px] text-foreground border ${
                  accentHsl
                    ? "bg-accent/15 border-accent/40"
                    : "bg-primary/5 border-primary/20"
                }`}
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <span
                    className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${
                      accentHsl ? "bg-accent" : "bg-primary"
                    }`}
                  />
                  <span
                    className={`relative inline-flex rounded-full h-2 w-2 ${
                      accentHsl ? "bg-accent" : "bg-primary"
                    }`}
                  />
                </span>
                <span>
                  <strong className="font-semibold">7</strong> meetings booked with {company} this week
                </span>
              </div>

              {/* Calendar nav */}
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  className="bg-muted text-foreground/80 px-2.5 py-1 rounded-md text-xs"
                  aria-label="Previous month"
                >
                  ←
                </button>
                <span className="font-semibold text-foreground text-[13px]">{monthName}</span>
                <button
                  type="button"
                  className="bg-muted text-foreground px-2.5 py-1 rounded-md text-xs"
                  aria-label="Next month"
                >
                  →
                </button>
              </div>

              {/* Day grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {DAY_HEADERS.map((d, i) => (
                  <div key={`h-${i}`} className="text-center text-[10px] text-muted-foreground py-1.5 font-semibold">
                    {d}
                  </div>
                ))}
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`e-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const d = i + 1;
                  const isPast = d < today;
                  const isToday = d === today;
                  const isSelected = d === selectedDay;
                  return (
                    <button
                      key={d}
                      type="button"
                      disabled={isPast}
                      onClick={() => setSelectedDay(d)}
                      className={`text-center py-1.5 rounded-md text-xs transition-all ${
                        isSelected
                          ? "bg-primary text-primary-foreground font-semibold"
                          : isPast
                          ? "text-muted-foreground opacity-30 cursor-not-allowed"
                          : "text-foreground hover:bg-muted"
                      } ${isToday && !isSelected ? "ring-1 ring-primary" : ""}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>

              {/* Time slots */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3 w-3" aria-hidden /> Available times
                  </p>
                  <p className="text-[10px] text-muted-foreground">Times shown in your timezone</p>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {TIMES.map(t => {
                    const isSel = t === selectedTime;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setSelectedTime(t)}
                        className={`py-1.5 rounded-md text-xs border transition-all ${
                          isSel
                            ? "bg-primary text-primary-foreground border-primary font-semibold ring-2 ring-primary/30"
                            : "bg-background text-foreground border-border hover:border-primary/40"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Meeting summary */}
              <div className="rounded-lg border border-border bg-muted/40 p-3 mb-4 space-y-1.5 text-[12px]">
                <div className="flex items-center gap-2 text-foreground">
                  <Calendar className="h-3.5 w-3.5 text-primary" aria-hidden />
                  <span className="font-medium">
                    {new Date(year, month, selectedDay).toLocaleDateString(undefined, {
                      weekday: "long",
                      month: "short",
                      day: "numeric",
                    })}{" "}
                    · {selectedTime}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Video className="h-3.5 w-3.5" aria-hidden />
                  <span>15-min intro · Google Meet link sent on confirm</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  <span>Qualification questions screen out bad fits</span>
                </div>
              </div>

              {/* Sign-up nudge — themed with brand */}
              <div
                className={`rounded-lg border bg-gradient-to-br p-3 text-center ${
                  accentHsl
                    ? "border-accent/40 from-accent/15 via-primary/5 to-transparent"
                    : "border-primary/30 from-primary/10 to-transparent"
                }`}
              >
                <p className="text-[12px] text-foreground font-medium mb-0.5">
                  This is a preview. Your real booking page launches when you sign up.
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Branded with your logo, synced to your calendar, qualifying every request.
                </p>
              </div>
            </div>
          </div>

      <p className="mt-2 text-center text-[11px] text-muted-foreground">
        Hosted by Auto Pitch · Google Calendar &amp; Microsoft 365 supported
      </p>

      {/* Inline-mode affordance — lets the visitor pop the same preview
          into a fullscreen lightbox for a closer look. Only renders when
          a parent passes onOpenFullscreen (i.e. we're embedded inline). */}
      {compact && onOpenFullscreen && (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            onClick={onOpenFullscreen}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Open full screen
            <ArrowUpRight className="h-3 w-3" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Lightbox wrapper — renders the same body inside a Dialog for visitors
 * who want a closer look. Inline embeds use BookingPagePreviewBody directly.
 */
export function BookingPagePreviewLightbox({
  open,
  onOpenChange,
  ...rest
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-label="Branded booking page preview"
        className="max-w-[520px] max-h-[calc(100dvh-1rem)] overflow-y-auto p-0 bg-transparent border-0 shadow-none !top-2 !translate-y-0 sm:max-h-[calc(100dvh-2rem)] sm:!top-1/2 sm:!translate-y-[-50%] [&>button]:hidden"
      >
        <DialogTitle className={srOnly}>Branded booking page preview</DialogTitle>
        <DialogDescription className={srOnly}>
          Preview of the booking page {rest.company} would get with Auto Pitch.
        </DialogDescription>
        <BookingPagePreviewBody
          {...rest}
          compact={false}
          onOpenFullscreen={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
